import type { DayOfWeek, PricingRule, LocationPricingRuleDto, RoundingMode } from "../../api/location.ts";

export type { DayOfWeek, PricingRule, LocationPricingRuleDto, RoundingMode };

export const ALL_DAYS: DayOfWeek[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export interface PricingRuleErrors {
  days?: string;
  startTime?: string;
  endTime?: string;
  percentage?: string;
  roundingEnding?: string;
  overlap?: string;
}

export interface PricingRulesValidationResult {
  ruleErrors: Map<string, PricingRuleErrors>;
  hasErrors: boolean;
}

export function validatePricingRule(rule: PricingRule): PricingRuleErrors {
  const errors: PricingRuleErrors = {};

  if (rule.days.length === 0) {
    errors.days = "Select at least one day.";
  }

  if (!rule.startTime) {
    errors.startTime = "Start time is required.";
  }

  if (!rule.endTime) {
    errors.endTime = "End time is required.";
  }

  if (rule.startTime && rule.endTime && rule.endTime <= rule.startTime) {
    errors.endTime = "End time must be later than start time.";
  }

  if (rule.percentage === undefined || rule.percentage === null || isNaN(rule.percentage)) {
    errors.percentage = "Enter a percentage adjustment.";
  } else if (rule.percentage < -50 || rule.percentage > 1000) {
    errors.percentage = "Percentage must be between -50% and 1000%.";
  }

  if (rule.roundingMode === "ending") {
    if (rule.roundingEnding === undefined || rule.roundingEnding === null || isNaN(rule.roundingEnding)) {
      errors.roundingEnding = "Enter the price ending to round to (e.g. 0.99).";
    } else if (rule.roundingEnding < 0 || rule.roundingEnding >= 1) {
      errors.roundingEnding = "Price ending must be between 0.00 and 0.99.";
    }
  }

  return errors;
}

export function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

export function rulesTimeOverlap(a: PricingRule, b: PricingRule): boolean {
  if (!a.startTime || !a.endTime || !b.startTime || !b.endTime) return false;
  const aStart = timeToMinutes(a.startTime);
  const aEnd = timeToMinutes(a.endTime);
  const bStart = timeToMinutes(b.startTime);
  const bEnd = timeToMinutes(b.endTime);
  // Strictly less-than: touching boundaries (aEnd === bStart) are NOT overlaps
  return aStart < bEnd && bStart < aEnd;
}

export function shareDay(a: PricingRule, b: PricingRule): DayOfWeek | null {
  for (const day of a.days) {
    if (b.days.includes(day)) return day;
  }
  return null;
}

export function detectPricingRuleOverlaps(rules: PricingRule[]): Map<string, string> {
  const errors = new Map<string, string>();

  for (let i = 0; i < rules.length; i++) {
    for (let j = i + 1; j < rules.length; j++) {
      const a = rules[i]!;
      const b = rules[j]!;
      const shared = shareDay(a, b);
      if (!shared) continue;
      if (!rulesTimeOverlap(a, b)) continue;

      if (a.startTime === b.startTime && a.endTime === b.endTime) {
        errors.set(a.id, "A pricing rule already exists for this period.");
        errors.set(b.id, "A pricing rule already exists for this period.");
      } else {
        errors.set(a.id, `This rule overlaps another pricing rule on ${shared}.`);
        errors.set(b.id, `This rule overlaps another pricing rule on ${shared}.`);
      }
    }
  }

  return errors;
}

export function validatePricingRules(rules: PricingRule[]): PricingRulesValidationResult {
  const ruleErrors = new Map<string, PricingRuleErrors>();
  const overlapErrors = detectPricingRuleOverlaps(rules);

  for (const rule of rules) {
    const errors = validatePricingRule(rule);
    const overlap = overlapErrors.get(rule.id);
    if (overlap) errors.overlap = overlap;
    if (Object.keys(errors).length > 0) {
      ruleErrors.set(rule.id, errors);
    }
  }

  return { ruleErrors, hasErrors: ruleErrors.size > 0 };
}

export function calculateAdjustedPrice(basePrice: number, percentage: number): number {
  return basePrice * (1 + percentage / 100);
}

/**
 * Rounds a price according to a rule's rounding mode. Mirrors the kiosk-side
 * VB implementation so the admin preview and the kiosk agree.
 *  - none    → unchanged
 *  - nearest → nearest whole number
 *  - down    → floor to whole number
 *  - ending  → nearest value of the form (integer + ending), e.g. x.99 / x.49
 * Never returns a negative price.
 */
export function applyRounding(price: number, mode: RoundingMode, ending: number): number {
  let result: number;
  switch (mode) {
    case "nearest":
      result = Math.round(price);
      break;
    case "down":
      result = Math.floor(price);
      break;
    case "ending": {
      const n = Math.round(price - ending);
      result = n + ending;
      // For a very small base price the nearest ending can fall below zero;
      // step up to the smallest non-negative ending instead of clamping to 0.
      if (result < 0) result = ending;
      break;
    }
    case "none":
    default:
      result = price;
      break;
  }
  return result < 0 ? 0 : result;
}

/** Full kiosk-equivalent price for a rule: apply the percentage, then round. */
export function applyRuleToPrice(basePrice: number, rule: PricingRule): number {
  const adjusted = calculateAdjustedPrice(basePrice, rule.percentage);
  return applyRounding(adjusted, rule.roundingMode, rule.roundingEnding);
}

export function getRulesForDay(rules: PricingRule[], day: DayOfWeek): PricingRule[] {
  return rules.filter((r) => r.days.includes(day));
}

/* ── DTO ↔ frontend mapping ──────────────────────────────── */

const DAY_KEYS: DayOfWeek[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const ROUNDING_MODES: RoundingMode[] = ["none", "nearest", "down", "ending"];

function parseRoundingMode(v: string | null | undefined): RoundingMode {
  const m = (v ?? "").toLowerCase();
  return (ROUNDING_MODES as string[]).includes(m) ? (m as RoundingMode) : "none";
}

/** Convert a DB row into the frontend PricingRule shape. */
export function dtoToRule(dto: LocationPricingRuleDto): PricingRule {
  const days: DayOfWeek[] = DAY_KEYS.filter((d) => dto[d]);
  return {
    id: String(dto.RuleId),
    days,
    startTime: dto.StartTime.substring(0, 5),   // "08:00:00.0000000" → "08:00"
    endTime: dto.EndTime.substring(0, 5),
    percentage: Number(dto.PricePct),
    roundingMode: parseRoundingMode(dto.RoundingMode),
    roundingEnding: Number(dto.RoundingEnding ?? 0),
  };
}

/** Convert frontend PricingRule[] into the DTO array the backend expects.
 *  Times are sent as "HH:MM:SS" so .NET's TimeSpan.Parse can deserialize them.
 *
 *  RuleId carries the persisted identity so the backend can diff: a rule loaded
 *  from the server has a numeric `id` (its RuleId), while a newly-added or
 *  duplicated rule has a UUID `id` → sent as RuleId 0 so the backend inserts it.
 *  Unchanged existing rules keep their RuleId and are left untouched server-side. */
export function rulesToDtos(locationId: number, rules: PricingRule[]): LocationPricingRuleDto[] {
  return rules.map((r) => ({
    RuleId: /^\d+$/.test(r.id) ? Number(r.id) : 0,
    LocationId: locationId,
    StartTime: r.startTime ? r.startTime + ":00" : r.startTime,
    EndTime: r.endTime ? r.endTime + ":00" : r.endTime,
    PricePct: r.percentage,
    Mon: r.days.includes("Mon"),
    Tue: r.days.includes("Tue"),
    Wed: r.days.includes("Wed"),
    Thu: r.days.includes("Thu"),
    Fri: r.days.includes("Fri"),
    Sat: r.days.includes("Sat"),
    Sun: r.days.includes("Sun"),
    RoundingMode: r.roundingMode,
    RoundingEnding: r.roundingMode === "ending" ? r.roundingEnding : 0,
  }));
}

/**
 * Canonical signature of a rule set for change-detection. Ignores volatile
 * client ids (new rules get UUIDs, loaded rules get their RuleId) and rule/day
 * ordering, so only persisted *content* drives "did this change". Lets the
 * location save skip the pricing-rules write entirely when nothing changed —
 * which keeps a plain location create/edit from touching the optional, per-DB
 * Kiosk_Time_Based_Pricing table (absent on some databases).
 */
export function pricingRulesSignature(rules: PricingRule[]): string {
  const norm = rules.map((r) => ({
    days: [...r.days].sort(),
    startTime: r.startTime,
    endTime: r.endTime,
    percentage: r.percentage,
    roundingMode: r.roundingMode,
    roundingEnding: r.roundingMode === "ending" ? r.roundingEnding : 0,
  }));
  norm.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  return JSON.stringify(norm);
}

export function getPreviewForDay(
  rules: PricingRule[],
  day: DayOfWeek,
): { startTime: string; endTime: string; percentage: number }[] {
  return getRulesForDay(rules, day)
    .filter((r) => r.startTime && r.endTime)
    .map((r) => ({ startTime: r.startTime, endTime: r.endTime, percentage: r.percentage }))
    .sort((a, b) => a.startTime.localeCompare(b.startTime));
}
